import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /*
   * ------------------------------------------------------------
   * Configuration
   * ------------------------------------------------------------
   */

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) {
        this.#onQuantityChange(event);
      }
    },
    300
  );

  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * Maximum amount of time we allow a cart request to remain active
   * before releasing the disabled state.
   *
   * This prevents the cart from getting permanently stuck on
   * "Updating..." if Shopify or a section render hangs.
   */
  #cartUpdateTimeout = 15000;

  /*
   * ------------------------------------------------------------
   * Lifecycle
   * ------------------------------------------------------------
   */

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );

    document.addEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate
    );

    document.addEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate
    );

    /*
     * Subscription controls are delegated from the document so that
     * they continue working after cart sections are morphed.
     */
    document.addEventListener(
      'click',
      this.#handleSubscriptionUpgradeClick
    );

    document.addEventListener(
      'change',
      this.#handleFrequencyChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    );

    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );

    document.removeEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate
    );

    document.removeEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate
    );

    document.removeEventListener(
      'click',
      this.#handleSubscriptionUpgradeClick
    );

    document.removeEventListener(
      'change',
      this.#handleFrequencyChange
    );
  }

  /*
   * ------------------------------------------------------------
   * External cart update detection
   * ------------------------------------------------------------
   */

  /**
   * True when the event was dispatched from outside this cart-items-component.
   *
   * @param {Event} event
   * @returns {boolean}
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /*
   * ------------------------------------------------------------
   * Discount updates
   * ------------------------------------------------------------
   */

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];

        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });

          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-items] Event promise rejected:',
            error
          );
        }
      });
  };

  /*
   * ------------------------------------------------------------
   * Cart note updates
   * ------------------------------------------------------------
   */

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;

    event.promise
      ?.then(({ detail }) => {
        const sections =
          /** @type {Record<string, string> | undefined} */
          (detail?.sections);

        const sectionsHtml = sections?.[this.sectionId];

        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-items] Event promise rejected:',
            error
          );
        }
      });
  };

  /*
   * ------------------------------------------------------------
   * Quantity selector
   * ------------------------------------------------------------
   */

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    if (
      !(event.target instanceof Node) ||
      !this.contains(event.target)
    ) {
      return;
    }

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent =
      /** @type {TextComponent | undefined} */
      (lineItemRow.querySelector('text-component'));

    textComponent?.shimmer();
  }

  /**
   * Public helper for changing a cart line quantity.
   *
   * This can be called from other cart controls:
   *
   * cartItems.updateLineQuantity(line, quantity)
   *
   * @param {number} line
   * @param {number} quantity
   */
  updateLineQuantity(line, quantity) {
    if (!line || quantity == null) return;

    const numericLine = Number(line);
    const numericQuantity = Number(quantity);

    if (!Number.isFinite(numericLine)) return;
    if (!Number.isFinite(numericQuantity)) return;

    this.updateQuantity({
      line: numericLine,
      quantity: numericQuantity,
      action: numericQuantity === 0 ? 'clear' : 'change',
    });
  }

  /*
   * ------------------------------------------------------------
   * Remove line
   * ------------------------------------------------------------
   */

  /**
   * Handles the line item removal.
   *
   * @param {number} line
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove =
      this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,

      ...this.refs.cartItemRows.filter(
        (row) =>
          row.dataset.parentKey ===
          cartItemRowToRemove.dataset.key
      ),
    ];

    const isEmptyCart =
      rowsToRemove.length ===
      this.refs.cartItemRows.length;

    const template =
      document.getElementById('empty-cart-template');

    if (
      isEmptyCart &&
      template instanceof HTMLTemplateElement
    ) {
      const clone = document.importNode(
        template.content,
        true
      );

      startViewTransition(
        () => {
          document
            .getElementById('cart-drawer-heading')
            ?.remove();

          this.replaceChildren(clone);
        },
        [
          this.isDrawer
            ? 'empty-cart-drawer'
            : 'empty-cart-page',
        ]
      );

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        return remove();
      }

      row.style.setProperty(
        '--row-height',
        `${row.clientHeight}px`
      );

      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /*
   * ------------------------------------------------------------
   * Main cart quantity update
   * ------------------------------------------------------------
   */

  /**
   * Updates the quantity.
   *
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  async updateQuantity(config) {
    const cartPerformanceUpdateMarker =
      cartPerformance.createStartingMarker(
        `${config.action}:user-action`
      );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const sectionsToUpdate = new Set([
      this.sectionId,
    ]);

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        sectionsToUpdate.add(
          item.dataset.sectionId
        );
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise =
      CartLinesUpdateEvent.createPromise();

    const lineId =
      this.refs.cartItemRows[line - 1]
        ?.dataset.key ?? '';

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action:
          config.action === 'change' &&
          quantity > 0
            ? 'update'
            : 'remove',

        context: 'cart',

        lines: [
          {
            id: lineId,
            quantity,
          },
        ],

        promise:
          deferredUpdatePromise.promise,
      })
    );

    /*
     * Timeout protection.
     *
     * If the Shopify request or section morph gets stuck,
     * make sure the cart isn't permanently disabled.
     */
    let timeoutId = null;

    const timeoutPromise = new Promise(
      (_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(
            new Error(
              'Cart update timed out. Please try again.'
            )
          );
        }, this.#cartUpdateTimeout);
      }
    );

    try {
      const requestPromise = fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', {
          body,
        })
      ).then((response) => {
        if (!response.ok) {
          throw new Error(
            `Cart update failed: ${response.status}`
          );
        }

        return response.text();
      });

      const responseText =
        await Promise.race([
          requestPromise,
          timeoutPromise,
        ]);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      let parsedResponseText;

      try {
        parsedResponseText =
          JSON.parse(responseText);
      } catch (error) {
        throw new Error(
          'Invalid response received from Shopify cart.'
        );
      }

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(
          line,
          parsedResponseText
        );

        deferredUpdatePromise.reject(
          new Error(parsedResponseText.errors)
        );

        return;
      }

      /*
       * Make sure Shopify actually returned the section
       * we requested before attempting to morph it.
       */
      const sectionHTML =
        parsedResponseText.sections?.[
          this.sectionId
        ];

      if (!sectionHTML) {
        throw new Error(
          'Cart updated, but the cart section was not returned.'
        );
      }

      const newSectionHTML =
        new DOMParser().parseFromString(
          sectionHTML,
          'text/html'
        );

      const newCartHiddenItemCount =
        newSectionHTML.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;

      const newCartItemCount =
        newCartHiddenItemCount
          ? parseInt(
              newCartHiddenItemCount,
              10
            )
          : 0;

      /*
       * Update quantity data attributes before morphing.
       */
      this.#updateQuantitySelectors(
        parsedResponseText
      );

      /*
       * Resolve the event before morphing.
       *
       * This is important because other cart components
       * can be listening to this promise.
       */
      deferredUpdatePromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            parsedResponseText
          ),

        detail: {
          sections:
            parsedResponseText.sections,

          items:
            parsedResponseText.items,

          itemCount:
            newCartItemCount,

          source:
            'cart-items-component',

          didError: false,
        },
      });

      /*
       * Morph the cart section.
       */
      await morphSection(
        this.sectionId,
        sectionHTML,
        {
          mode:
            this.isDrawer
              ? 'hydration'
              : 'full',
        }
      );

      /*
       * Re-enable quantity controls after the morph.
       */
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(
        '[cart-items] Quantity update failed:',
        error
      );

      /*
       * Always resolve the disabled state.
       */
      resetShimmer(this);

      /*
       * Do not leave the deferred event hanging.
       */
      try {
        deferredUpdatePromise.reject(error);
      } catch (promiseError) {
        console.warn(
          '[cart-items] Unable to reject update promise:',
          promiseError
        );
      }

      /*
       * Tell the rest of the theme about the failure.
       */
      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Failed to update cart',

          code:
            error?.message?.includes('timed out')
              ? 'TIMEOUT'
              : 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      /*
       * This is the important part for the
       * "Updating..." problem.
       */
      this.#enableCartItems();

      cartPerformance.measureFromMarker(
        cartPerformanceUpdateMarker
      );
    }
  }

  /*
   * ------------------------------------------------------------
   * Cart errors
   * ------------------------------------------------------------
   */

  /**
   * Handles cart errors.
   *
   * @param {number} line
   * @param {Object} parsedResponseText
   */
  #handleCartError = (
    line,
    parsedResponseText
  ) => {
    const quantitySelector =
      this.refs.quantitySelectors?.[
        line - 1
      ];

    const quantityInput =
      quantitySelector?.querySelector(
        'input'
      );

    if (quantityInput) {
      quantityInput.value =
        quantityInput.defaultValue;
    }

    const cartItemError =
      this.refs[
        `cartItemError-${line}`
      ];

    const cartItemErrorContainer =
      this.refs[
        `cartItemErrorContainer-${line}`
      ];

    if (
      cartItemError instanceof HTMLElement
    ) {
      cartItemError.textContent =
        parsedResponseText.errors ||
        'Cart update failed';
    }

    if (
      cartItemErrorContainer instanceof
      HTMLElement
    ) {
      cartItemErrorContainer.classList.remove(
        'hidden'
      );
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error:
          parsedResponseText.errors ||
          'Cart update failed',

        code: 'INVALID',
      })
    );
  };

  /*
   * ------------------------------------------------------------
   * External cart line updates
   * ------------------------------------------------------------
   */

  /**
   * Handles CartLinesUpdateEvent.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections =
          detail?.sections;

        const cartItemsHtml =
          sections?.[this.sectionId];

        const wasEmptyCartDrawer =
          this.isDrawer &&
          this.querySelector(
            '[data-cart-drawer-empty]'
          ) !== null;

        /** @type {'hydration' | 'full'} */
        const mode =
          this.isDrawer
            ? 'hydration'
            : 'full';

        const morphOptions = {
          mode,
          injectStylesheet:
            wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys =
            new Set(
              this.refs.cartItemRows?.map(
                (row) => row.dataset.key
              ) ?? []
            );

          if (wasEmptyCartDrawer) {
            startViewTransition(
              () => {
                morphSection(
                  this.sectionId,
                  cartItemsHtml,
                  morphOptions
                );
              },
              ['fill-cart-drawer']
            );
          } else {
            await morphSection(
              this.sectionId,
              cartItemsHtml,
              morphOptions
            );
          }

          if (
            !wasEmptyCartDrawer &&
            !prefersReducedMotion()
          ) {
            for (
              const row of
              this.refs.cartItemRows ?? []
            ) {
              if (
                !existingKeys.has(
                  row.dataset.key
                )
              ) {
                row.classList.add(
                  'adding'
                );

                onAnimationEnd(
                  row,
                  () =>
                    row.classList.remove(
                      'adding'
                    )
                );
              }
            }
          }

          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(
            this.sectionId,
            {
              cache: false,
              ...morphOptions,
            }
          );
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-items] Event promise rejected:',
            error
          );
        }
      });
  };

  /*
   * ------------------------------------------------------------
   * Cart enabled / disabled state
   * ------------------------------------------------------------
   */

  #disableCartItems() {
    this.classList.add(
      'cart-items-disabled'
    );

    /*
     * Disable subscription controls as well.
     */
    this.#setSubscriptionControlsDisabled(
      true
    );
  }

  #enableCartItems() {
    this.classList.remove(
      'cart-items-disabled'
    );

    /*
     * Always restore subscription controls.
     */
    this.#setSubscriptionControlsDisabled(
      false
    );
  }

  /**
   * Enables/disables subscription controls.
   *
   * @param {boolean} disabled
   */
  #setSubscriptionControlsDisabled(
    disabled
  ) {
    document
      .querySelectorAll(
        '[data-cart-subscription-upgrade], [data-cart-frequency]'
      )
      .forEach((element) => {
        if (
          element instanceof HTMLButtonElement ||
          element instanceof HTMLSelectElement
        ) {
          element.disabled = disabled;
        }
      });
  }

  /*
   * ------------------------------------------------------------
   * Quantity selector state
   * ------------------------------------------------------------
   */

  /**
   * Updates quantity selectors for all matching variants.
   *
   * @param {Object} updatedCart
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items]
   */
  #updateQuantitySelectors(
    updatedCart
  ) {
    if (!updatedCart.items) return;

    for (
      const item of updatedCart.items
    ) {
      const variantId =
        item.variant_id.toString();

      const selectors =
        document.querySelectorAll(
          `quantity-selector-component[data-variant-id="${variantId}"]`
        );

      for (
        const selector of selectors
      ) {
        const input =
          selector.querySelector(
            'input[data-cart-quantity]'
          );

        if (!input) continue;

        input.setAttribute(
          'data-cart-quantity',
          item.quantity.toString()
        );

        if (
          'updateCartQuantity' in
            selector &&
          typeof selector.updateCartQuantity ===
            'function'
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all quantity selectors.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (
      const selector of document.querySelectorAll(
        'cart-quantity-selector-component'
      )
    ) {
      /** @type {any} */
      const component = selector;

      component.updateButtonStates?.();
    }
  }

  /*
   * ------------------------------------------------------------
   * Subscription upgrade
   * ------------------------------------------------------------
   *
   * Expected markup:
   *
   * <button
   *   data-cart-subscription-upgrade
   *   data-line="2"
   *   data-selling-plan-id="123456"
   * >
   *   Upgrade to Subscription & Save
   * </button>
   *
   */

  #handleSubscriptionUpgradeClick = (
    event
  ) => {
    const target =
      event.target instanceof Element
        ? event.target.closest(
            '[data-cart-subscription-upgrade]'
          )
        : null;

    if (!target) return;

    /*
     * Only handle controls belonging to this
     * cart component.
     */
    if (!this.contains(target)) return;

    event.preventDefault();

    if (
      target instanceof HTMLButtonElement &&
      target.disabled
    ) {
      return;
    }

    const line =
      Number(
        target.getAttribute(
          'data-line'
        )
      );

    const sellingPlanId =
      target.getAttribute(
        'data-selling-plan-id'
      );

    if (!line) {
      console.warn(
        '[cart-items] Subscription upgrade is missing data-line.'
      );

      return;
    }

    if (!sellingPlanId) {
      console.warn(
        '[cart-items] Subscription upgrade is missing data-selling-plan-id.'
      );

      return;
    }

    this.#upgradeLineToSubscription(
      line,
      sellingPlanId,
      target
    );
  };

  /**
   * Upgrades an existing one-time cart line
   * to a subscription.
   *
   * Because Shopify cart/change cannot simply change
   * the selling plan of an existing line, this method:
   *
   * 1. Reads the existing line.
   * 2. Adds the same variant with a selling plan.
   * 3. Removes the original line.
   * 4. Refreshes the cart.
   *
   * @param {number} line
   * @param {string} sellingPlanId
   * @param {Element} button
   */
  async #upgradeLineToSubscription(
    line,
    sellingPlanId,
    button
  ) {
    if (
      button instanceof HTMLButtonElement
    ) {
      button.disabled = true;
      button.dataset.originalText =
        button.textContent || '';

      button.textContent =
        'Updating...';
    }

    this.#disableCartItems();

    try {
      const cart =
        await this.fetchCartData();

      const item =
        cart.items?.[line - 1];

      if (!item) {
        throw new Error(
          'Cart item could not be found.'
        );
      }

      /*
       * Add the same variant as a subscription.
       */
      const addResponse =
        await fetch(
          '/cart/add.js',
          fetchConfig('json', {
            body: JSON.stringify({
              items: [
                {
                  id: item.variant_id,
                  quantity: item.quantity,
                  selling_plan:
                    Number(
                      sellingPlanId
                    ),
                  properties:
                    item.properties || {},
                },
              ],
            }),
          })
        );

      if (!addResponse.ok) {
        throw new Error(
          'Unable to add subscription to cart.'
        );
      }

      await addResponse.json();

      /*
       * Fetch the cart again because adding the
       * subscription can alter line positions.
       */
      const updatedCart =
        await this.fetchCartData();

      /*
       * Find the original one-time line.
       *
       * We look for the same variant without
       * the selected selling plan.
       */
      const originalLine =
        updatedCart.items?.find(
          (cartItem) =>
            cartItem.variant_id ===
              item.variant_id &&
            !cartItem.selling_plan_allocation
        );

      if (originalLine) {
        await fetch(
          `${Theme.routes.cart_change_url}`,
          fetchConfig('json', {
            body: JSON.stringify({
              id: originalLine.key,
              quantity: 0,
              sections:
                this.sectionId,
              sections_url:
                window.location.pathname,
            }),
          })
        );
      }

      /*
       * Refresh the cart section.
       */
      await this.#refreshCartSection();

    } catch (error) {
      console.error(
        '[cart-items] Subscription upgrade failed:',
        error
      );

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Unable to upgrade to subscription.',
          code: 'SUBSCRIPTION_UPGRADE_FAILED',
        })
      );
    } finally {
      this.#enableCartItems();

      if (
        button instanceof HTMLButtonElement
      ) {
        button.disabled = false;

        button.textContent =
          button.dataset.originalText ||
          'Upgrade to Subscription & Save';
      }
    }
  }

  /*
   * ------------------------------------------------------------
   * Delivery frequency
   * ------------------------------------------------------------
   *
   * Expected markup:
   *
   * <select
   *   data-cart-frequency
   *   data-line="1"
   * >
   *   <option value="123">Deliver every month</option>
   *   <option value="456">Deliver every 2 months</option>
   * </select>
   *
   */

  #handleFrequencyChange = (
    event
  ) => {
    const target =
      event.target instanceof HTMLSelectElement
        ? event.target.closest(
            '[data-cart-frequency]'
          )
        : null;

    if (
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }

    if (!this.contains(target)) return;

    const line =
      Number(
        target.getAttribute(
          'data-line'
        )
      );

    const sellingPlanId =
      target.value;

    if (!line) {
      console.warn(
        '[cart-items] Frequency selector is missing data-line.'
      );

      return;
    }

    if (!sellingPlanId) return;

    this.#changeSubscriptionFrequency(
      line,
      sellingPlanId,
      target
    );
  };

  /**
   * Changes the selling plan of an existing
   * subscription line.
   *
   * @param {number} line
   * @param {string} sellingPlanId
   * @param {HTMLSelectElement} select
   */
  async #changeSubscriptionFrequency(
    line,
    sellingPlanId,
    select
  ) {
    if (select.disabled) return;

    select.disabled = true;

    const originalValue =
      select.dataset.currentValue ||
      select.value;

    select.dataset.currentValue =
      select.value;

    this.#disableCartItems();

    try {
      const cart =
        await this.fetchCartData();

      const item =
        cart.items?.[line - 1];

      if (!item) {
        throw new Error(
          'Cart item could not be found.'
        );
      }

      /*
       * If the line is already on a selling plan,
       * we need to replace it with the same product
       * using the new selling plan.
       */
      const addResponse =
        await fetch(
          '/cart/add.js',
          fetchConfig('json', {
            body: JSON.stringify({
              items: [
                {
                  id: item.variant_id,
                  quantity: item.quantity,
                  selling_plan:
                    Number(
                      sellingPlanId
                    ),
                  properties:
                    item.properties || {},
                },
              ],
            }),
          })
        );

      if (!addResponse.ok) {
        throw new Error(
          'Unable to change delivery frequency.'
        );
      }

      await addResponse.json();

      /*
       * Fetch the updated cart so we can
       * identify the old subscription line.
       */
      const updatedCart =
        await this.fetchCartData();

      const oldLine =
        updatedCart.items?.find(
          (cartItem) =>
            cartItem.variant_id ===
              item.variant_id &&
            cartItem.key !== item.key
        );

      /*
       * Remove the old line if Shopify created
       * a new line.
       */
      if (oldLine) {
        await fetch(
          `${Theme.routes.cart_change_url}`,
          fetchConfig('json', {
            body: JSON.stringify({
              id: item.key,
              quantity: 0,
              sections:
                this.sectionId,
              sections_url:
                window.location.pathname,
            }),
          })
        );
      }

      await this.#refreshCartSection();

    } catch (error) {
      console.error(
        '[cart-items] Frequency change failed:',
        error
      );

      /*
       * Restore previous selection.
       */
      select.value =
        originalValue;

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Unable to change delivery frequency.',
          code: 'FREQUENCY_CHANGE_FAILED',
        })
      );
    } finally {
      select.disabled = false;

      this.#enableCartItems();
    }
  }

  /*
   * ------------------------------------------------------------
   * Refresh cart section
   * ------------------------------------------------------------
   */

  async #refreshCartSection() {
    const response =
      await fetch(
        `${Theme.routes.cart_url}.js`,
        {
          headers: {
            Accept:
              'application/json',
          },
          credentials:
            'same-origin',
          cache: 'no-store',
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh cart.'
      );
    }

    /*
     * Ask Shopify for the cart section again.
     */
    const sectionResponse =
      await fetch(
        `${window.location.pathname}?sections=${encodeURIComponent(
          this.sectionId
        )}`,
        {
          headers: {
            Accept:
              'text/html',
          },
          credentials:
            'same-origin',
          cache: 'no-store',
        }
      );

    if (!sectionResponse.ok) {
      throw new Error(
        'Unable to refresh cart section.'
      );
    }

    const sectionData =
      await sectionResponse.json();

    const sectionHTML =
      sectionData?.[this.sectionId];

    if (!sectionHTML) {
      /*
       * Fallback to the theme section renderer.
       */
      await sectionRenderer.renderSection(
        this.sectionId,
        {
          cache: false,
          mode:
            this.isDrawer
              ? 'hydration'
              : 'full',
        }
      );

      return;
    }

    await morphSection(
      this.sectionId,
      sectionHTML,
      {
        mode:
          this.isDrawer
            ? 'hydration'
            : 'full',
      }
    );

    this.#updateCartQuantitySelectorButtonStates();
  }

  /*
   * ------------------------------------------------------------
   * Cart JSON
   * ------------------------------------------------------------
   */

  async fetchCartData() {
    if (this.#pendingCartFetch) {
      return this.#pendingCartFetch;
    }

    this.#pendingCartFetch =
      (async () => {
        const response =
          await fetch(
            `${Theme.routes.cart_url}.json`,
            {
              headers: {
                Accept:
                  'application/json',
              },
              credentials:
                'same-origin',
              cache: 'no-store',
            }
          );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch cart: ${response.status} ${response.statusText}`
          );
        }

        return response.json();
      })().finally(() => {
        this.#pendingCartFetch = null;
      });

    return this.#pendingCartFetch;
  }

  /*
   * ------------------------------------------------------------
   * Section ID
   * ------------------------------------------------------------
   */

  /**
   * Gets the section id.
   *
   * @returns {string}
   */
  get sectionId() {
    const {
      sectionId,
    } = this.dataset;

    if (!sectionId) {
      throw new Error(
        'Section id missing'
      );
    }

    return sectionId;
  }

  /*
   * ------------------------------------------------------------
   * Drawer
   * ------------------------------------------------------------
   */

  /**
   * @returns {boolean}
   */
  get isDrawer() {
    return (
      this.dataset.drawer !==
      undefined
    );
  }
}

/*
 * ------------------------------------------------------------
 * Custom element registration
 * ------------------------------------------------------------
 */

if (
  !customElements.get(
    'cart-items-component'
  )
) {
  customElements.define(
    'cart-items-component',
    CartItemsComponent
  );
}